import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ApiUrls } from '@api/apiUrls';
import { PspOperationList } from '@components-member/Sales/PspOperations';
import { keyMap } from '@locales/lokalise/keyMap';
import { fetchApi } from 'api/fetchApi';
import CONSTANTS from 'components/shared/constants';
import DetailsTable from 'components/shared/detailsTable';
import ErrorView from 'components/shared/errorView';
import NavButtons from 'components/shared/navigationButtons';
import Preloader from 'components/shared/preloader';
import { Tabs, TabList, TabPanel, TabListItem } from 'components/shared/tabs';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { handleError } from 'utils/errors';
import { SALES_USER_TRADING_ACCOUNT_DETAILS_COLUMNS } from './constants';
import type { ISalesUserTradingAccount } from './interfaces';

const TradingAccountDetails = () => {
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [details, setDetails] = useState<ISalesUserTradingAccount | null>(null);
  const [processing, setProcessing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const getModel = useCallback(async (): Promise<void> => {
    try {
      const response = await fetchApi.get<ISalesUserTradingAccount>(ApiUrls.salesUserTradingAccount.get, {
        params: { id },
      });

      setDetails(response.data);
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToGetRecord, error);
      setErrorMessage(error.response.data);
    } finally {
      setProcessing(false);
    }
  }, [id]);

  useEffect(() => {
    void getModel();
  }, [getModel]);

  const renderDetails = () => {
    if (processing) {
      return <Preloader />;
    }

    if (errorMessage) {
      return <ErrorView error={errorMessage} />;
    }

    if (!details) {
      return <></>;
    }

    return (
      <>
        <div className="form-box">
          <DetailsTable columns={SALES_USER_TRADING_ACCOUNT_DETAILS_COLUMNS} data={details} />
        </div>

        <div className="form-box">
          <Tabs className="horizontal--tabs">
            <TabList>
              <TabListItem title={keyMap.salesClientsDepositTitle} />
              <TabListItem title={keyMap.salesClientsWithdrawalTitle} />
            </TabList>
            <TabPanel>
              <PspOperationList type={CONSTANTS.PSP_OPERATIONS.DEPOSIT} tradingAccountId={id} />
              <PspOperationList type={CONSTANTS.PSP_OPERATIONS.WITHDRAWAL} tradingAccountId={id} />
            </TabPanel>
          </Tabs>
        </div>
      </>
    );
  };

  return (
    <div className="content">
      <div className="content__header">
        <NavButtons />
        <h1 className="content__title">{`${i18n.t(keyMap.sharedFieldsTradingAccount)} #${id}`}</h1>

        <div className="panel">{renderDetails()}</div>
      </div>
    </div>
  );
};

const TradingAccountDetailsWithErrorBoundary = withErrorBoundary(TradingAccountDetails);

export { TradingAccountDetailsWithErrorBoundary as TradingAccountDetails };
